import { DocumentData, Timestamp } from 'firebase/firestore';
import { pruneResultDataDiff } from './resultDataDiff';

/** result ドキュメント内で OCR 結果本体を保持するフィールド名。 */
const RESULT_DATA_KEY = 'result_data';

/**
 * 変更フィールドの差分を検出
 * @param previousData 変更前のデータ
 * @param newData 変更後のデータ
 * @returns { changedFields, previousValues } 変更されたフィールドと前の値
 */
export function detectChangedFields(
  previousData: DocumentData,
  newData: DocumentData,
): {
  changedFields: Record<string, unknown>;
  previousValues: Record<string, unknown>;
} {
  const changedFields: Record<string, unknown> = {};
  const previousValues: Record<string, unknown> = {};

  // 新しいデータのキーをループ
  for (const key of Object.keys(newData)) {
    // システムフィールドは除外
    if (key === 'updated_at' || key === 'updated_by') {
      continue;
    }

    const newValue = newData[key];
    const previousValue = previousData[key];

    // 値が変更されている場合
    if (!isEqual(previousValue, newValue)) {
      // result_data は全体を積むと 1 履歴で 1MB を超えるため、
      // 変わった項目だけに間引く（#3322）。構造は保つので読み手は変わらない。
      if (key === RESULT_DATA_KEY) {
        const pruned = pruneResultDataDiff(previousValue, newValue);
        if (pruned) {
          changedFields[key] = pruned.next;
          previousValues[key] = pruned.previous;
          continue;
        }
      }
      changedFields[key] = newValue;
      previousValues[key] = previousValue;
    }
  }

  return { changedFields, previousValues };
}

/**
 * 2つの値が等しいかを判定（オブジェクト・配列の深い比較対応）
 */
function isEqual(value1: unknown, value2: unknown): boolean {
  // プリミティブ値の比較
  if (value1 === value2) {
    return true;
  }

  // null/undefined の比較
  if (value1 == null || value2 == null) {
    return value1 === value2;
  }

  // 型が異なる場合
  if (typeof value1 !== typeof value2) {
    return false;
  }

  // Firestore Timestamp の比較
  if (value1 instanceof Timestamp && value2 instanceof Timestamp) {
    return value1.toMillis() === value2.toMillis();
  }

  // Date型の比較
  if (value1 instanceof Date && value2 instanceof Date) {
    return value1.getTime() === value2.getTime();
  }

  // 配列の比較
  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) {
      return false;
    }
    for (let i = 0; i < value1.length; i++) {
      if (!isEqual(value1[i], value2[i])) {
        return false;
      }
    }
    return true;
  }

  // オブジェクトの比較
  if (typeof value1 === 'object' && typeof value2 === 'object') {
    const keys1 = Object.keys(value1 as Record<string, unknown>);
    const keys2 = Object.keys(value2 as Record<string, unknown>);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (
        !keys2.includes(key) ||
        !isEqual((value1 as Record<string, unknown>)[key], (value2 as Record<string, unknown>)[key])
      ) {
        return false;
      }
    }
    return true;
  }

  return false;
}

/**
 * ステータス変更の検出
 */
export function detectStatusChange(
  previousData: DocumentData,
  newData: DocumentData,
): {
  isStatusChanged: boolean;
  changedFields: Record<string, unknown>;
  previousValues: Record<string, unknown>;
} {
  const statusFields = ['status', 'processing_status', 'ocr_status'];
  const changedFields: Record<string, unknown> = {};
  const previousValues: Record<string, unknown> = {};
  let isStatusChanged = false;

  for (const field of statusFields) {
    if (field in newData && previousData[field] !== newData[field]) {
      changedFields[field] = newData[field];
      previousValues[field] = previousData[field];
      isStatusChanged = true;
    }
  }

  return { isStatusChanged, changedFields, previousValues };
}
